import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Switch } from '../ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  AlertOctagon,
  Ban,
  Building2,
  Clock,
  Landmark,
  Loader2,
  Mail,
  RefreshCw,
  Undo2,
  ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : '—');

const StatChip = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
    <div className={`p-2 rounded-lg ${accent}`}><Icon className="h-4 w-4" /></div>
    <div>
      <p className="text-lg font-semibold text-white leading-none">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  </div>
);

export default function OwnerAdminUnpaidSweep() {
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTest, setShowTest] = useState(false);
  const [showGrace, setShowGrace] = useState(true);
  const [busyIds, setBusyIds] = useState({}); // { workspace_id: 'nudge'|'lock'|'unlock' }

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/owner-admin/unpaid-accounts?include_test=${showTest}&include_grace=${showGrace}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAccounts(data.accounts || []);
        setStats(data.stats || null);
      } else {
        toast.error('Failed to load unpaid accounts');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load unpaid accounts');
    } finally {
      setLoading(false);
    }
  }, [showTest, showGrace]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const markBusy = (id, kind) => setBusyIds((b) => ({ ...b, [id]: kind }));
  const clearBusy = (id) => setBusyIds((b) => { const c = { ...b }; delete c[id]; return c; });

  const handleNudge = async (row) => {
    markBusy(row.workspace_id, 'nudge');
    try {
      const res = await fetch(
        `${API_URL}/api/owner-admin/unpaid-accounts/${row.workspace_id}/nudge`,
        { method: 'POST', credentials: 'include' },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Nudge failed');
      } else if (data.sent) {
        toast.success(`Nudge sent to ${row.owner_email || 'owner'}`);
      } else if (data.reason === 'already_nudged_today') {
        toast.info('Already nudged in the last 24h — skipped');
      } else if (data.reason === 'no_email') {
        toast.warning('Owner has no email on file');
      } else {
        toast.warning(`Not sent: ${data.reason || 'unknown reason'}`);
      }
    } catch (e) {
      console.error(e);
      toast.error('Nudge failed');
    } finally {
      clearBusy(row.workspace_id);
    }
  };

  const handleLockToggle = async (row) => {
    const kind = row.is_locked ? 'unlock' : 'lock';
    markBusy(row.workspace_id, kind);
    try {
      const res = await fetch(
        `${API_URL}/api/owner-admin/unpaid-accounts/${row.workspace_id}/${kind}`,
        { method: 'POST', credentials: 'include' },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || `${kind} failed`);
      } else {
        toast.success(kind === 'lock' ? 'Workspace locked' : 'Workspace unlocked');
        await fetchAccounts();
      }
    } catch (e) {
      console.error(e);
      toast.error(`${kind} failed`);
    } finally {
      clearBusy(row.workspace_id);
    }
  };

  const rows = useMemo(() => accounts, [accounts]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <AlertOctagon className="h-6 w-6 text-orange-400" />
            Unpaid Sweep
          </h1>
          <p className="text-muted-foreground">
            Existing workspaces using CountBucks without a paid plan — nudge or lock them
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchAccounts}
          className="border-white/20 text-muted-foreground/80"
          data-testid="unpaid-sweep-refresh"
        >
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="unpaid-sweep-stats">
          <StatChip icon={Building2} label="Flagged workspaces" value={stats.total} accent="bg-white/10 text-muted-foreground/80" />
          <StatChip icon={AlertOctagon} label="Hard-gated" value={stats.hard_gated} accent="bg-red-500/15 text-red-400" />
          <StatChip icon={Clock} label="In grace period" value={stats.grace} accent="bg-amber-500/15 text-amber-400" />
          <StatChip icon={Ban} label="Currently locked" value={stats.locked} accent="bg-slate-500/15 text-slate-300" />
        </div>
      )}

      {/* Filters */}
      <Card className="bg-card border-white/15">
        <CardContent className="pt-6 flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-muted-foreground/80 cursor-pointer">
            <Switch checked={showGrace} onCheckedChange={setShowGrace} data-testid="show-grace-toggle" />
            Include grace-period accounts
          </label>
          <label className="flex items-center gap-2 text-sm text-muted-foreground/80 cursor-pointer">
            <Switch checked={showTest} onCheckedChange={setShowTest} data-testid="show-test-toggle" />
            Include test accounts
          </label>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-card border-white/15">
        <CardHeader>
          <CardTitle className="text-white text-lg">Flagged accounts</CardTitle>
          <CardDescription className="text-muted-foreground">
            Sorted by grace period first, then longest time without a plan.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[1,2,3,4,5].map((i) => <Skeleton key={i} className="h-12 w-full bg-white/10" />)}
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">Nothing flagged — every workspace has a paying owner.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/15 hover:bg-white/[0.07]">
                  <TableHead className="text-muted-foreground">Owner / Workspace</TableHead>
                  <TableHead className="text-muted-foreground">State</TableHead>
                  <TableHead className="text-muted-foreground">Days w/o Plan</TableHead>
                  <TableHead className="text-muted-foreground">Last Login</TableHead>
                  <TableHead className="text-muted-foreground">Credits</TableHead>
                  <TableHead className="text-muted-foreground">Banks</TableHead>
                  <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const busyKind = busyIds[r.workspace_id];
                  return (
                    <TableRow
                      key={r.workspace_id}
                      className="border-white/15 hover:bg-white/[0.07]"
                      data-testid={`unpaid-row-${r.workspace_id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-orange-500/10">
                            <Building2 className="h-4 w-4 text-orange-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-white truncate">
                              {r.owner_name || r.owner_email || '(no owner)'}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {r.owner_email || '—'} · {r.workspace_name}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {r.is_locked ? (
                            <Badge className="w-fit bg-slate-500/20 text-slate-300 gap-1">
                              <Ban className="h-3 w-3" /> Locked
                            </Badge>
                          ) : r.in_grace ? (
                            <Badge className="w-fit bg-amber-500/20 text-amber-300 gap-1">
                              <Clock className="h-3 w-3" /> Grace · {r.days_until_hard_gate}d left
                            </Badge>
                          ) : (
                            <Badge className="w-fit bg-red-500/20 text-red-300 gap-1">
                              <AlertOctagon className="h-3 w-3" /> Hard-gated
                            </Badge>
                          )}
                          <span className="text-[11px] text-muted-foreground">
                            plan: {r.plan_label} · {r.subscription_status}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground/80">
                        {r.days_without_plan != null ? `${r.days_without_plan}d` : '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {fmtDate(r.last_login)}
                      </TableCell>
                      <TableCell className="text-muted-foreground/80">
                        {r.credits_used_this_period}/{r.monthly_credits || 0}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-muted-foreground/80">
                          <Landmark className="h-4 w-4 text-muted-foreground" />
                          {r.bank_accounts}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!r.owner_email || busyKind === 'nudge' || r.in_grace === undefined}
                            onClick={() => handleNudge(r)}
                            className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                            data-testid={`nudge-btn-${r.workspace_id}`}
                          >
                            {busyKind === 'nudge'
                              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              : <Mail className="h-4 w-4 mr-1" />}
                            Nudge
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!!busyKind}
                            onClick={() => handleLockToggle(r)}
                            className={r.is_locked
                              ? 'border-slate-500/30 text-slate-200 hover:bg-white/10'
                              : 'border-red-500/30 text-red-300 hover:bg-red-500/10'}
                            data-testid={`lock-btn-${r.workspace_id}`}
                          >
                            {busyKind === 'lock' || busyKind === 'unlock'
                              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              : (r.is_locked
                                ? <Undo2 className="h-4 w-4 mr-1" />
                                : <Ban className="h-4 w-4 mr-1" />)}
                            {r.is_locked ? 'Unlock' : 'Lock'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                            className="text-muted-foreground hover:text-white"
                          >
                            <Link to={`/owner-admin/customers/${r.workspace_id}`}>
                              <ExternalLink className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
